import {
  fetchRemoteCustomersSnapshot,
  type CustomersSnapshotPayload,
} from '../../sync/core/customersRpcClient';
import type { RemoteCustomerRow } from '../../sync/core/customerPullDomain';
import { CUSTOMER_WEB_REST_CACHE_MS } from '../../shell/session/sessionCacheTtl';

let cachedSnapshot: CustomersSnapshotPayload | null = null;
let cacheUpdatedAt = 0;
/** Pull remoto en vuelo — lectores concurrentes comparten la misma request. */
let snapshotPullInFlight: Promise<CustomersSnapshotPayload> | null = null;
/** Sube en cada write local; pulls que empezaron antes no pueden pisar la cache. */
let snapshotGeneration = 0;
/** Filas escritas en web-ram antes de que el próximo pull completo las traiga. */
const optimisticCustomers = new Map<string, RemoteCustomerRow>();

export function getWebCustomerSnapshotGeneration(): number {
  return snapshotGeneration;
}

export function getWebCustomerSnapshotCache(): CustomersSnapshotPayload | null {
  return cachedSnapshot;
}

function overlayOptimisticCustomers(customers: RemoteCustomerRow[]): RemoteCustomerRow[] {
  if (optimisticCustomers.size === 0) return customers;
  const byId = new Map(customers.map((row) => [String(row.id), row]));
  for (const [id, local] of optimisticCustomers) {
    const remote = byId.get(id);
    if (!remote) {
      byId.set(id, local);
      continue;
    }
    const localTs = Date.parse(local.updated_at ?? '') || 0;
    const remoteTs = Date.parse(remote.updated_at ?? '') || 0;
    if (localTs >= remoteTs) {
      byId.set(id, { ...remote, ...local });
    } else {
      optimisticCustomers.delete(id);
    }
  }
  return Array.from(byId.values());
}

function mergeCustomerRows(
  existing: RemoteCustomerRow[],
  incoming: RemoteCustomerRow[],
): RemoteCustomerRow[] {
  const byId = new Map(existing.map((row) => [String(row.id), row]));
  for (const row of incoming) {
    if (!row?.id) continue;
    const id = String(row.id);
    const prev = byId.get(id);
    if (!prev) {
      byId.set(id, row);
      continue;
    }
    const prevTs = Date.parse(prev.updated_at ?? '') || 0;
    const nextTs = Date.parse(row.updated_at ?? '') || 0;
    if (nextTs >= prevTs) byId.set(id, { ...prev, ...row });
  }
  return Array.from(byId.values());
}

/**
 * Upsert optimista tras alta/edición web: el cliente aparece en Customer/POS
 * aunque el pull en vuelo todavía no traiga la fila.
 */
export function upsertWebCustomerInCache(row: RemoteCustomerRow): void {
  if (!row?.id) return;
  const normalized: RemoteCustomerRow = {
    id: String(row.id),
    name: row.name ?? '',
    phone: row.phone ?? null,
    cuit: row.cuit ?? null,
    credit_limit: row.credit_limit ?? 0,
    balance: row.balance ?? 0,
    blocked: row.blocked ?? false,
    updated_at: row.updated_at ?? new Date().toISOString(),
    email: row.email ?? null,
    address: row.address ?? null,
    tax_category: row.tax_category ?? null,
    notes: row.notes ?? null,
    payment_terms_days: row.payment_terms_days ?? null,
    price_list_id: row.price_list_id ?? null,
  };
  optimisticCustomers.set(normalized.id, normalized);
  snapshotGeneration += 1;

  if (cachedSnapshot && cachedSnapshot.snapshot_complete !== false) {
    cachedSnapshot = {
      ...cachedSnapshot,
      customers: mergeCustomerRows(cachedSnapshot.customers, [normalized]),
      snapshot_complete: true,
    };
    cacheUpdatedAt = Date.now();
    return;
  }

  // Cache vacía: seed mínimo completo para que POS encuentre al cliente ya.
  cachedSnapshot = {
    customers: [normalized],
    customer_movements: [],
    sale_payment_allocations: [],
    snapshot_complete: true,
  };
  cacheUpdatedAt = Date.now();
}

/** Ajuste local de saldo (cargo + / cobro −) hasta que el refresh remoto confirme. */
export function adjustWebCustomerBalanceInCache(customerId: string, delta: number): void {
  const id = customerId.trim();
  if (!id || !Number.isFinite(delta) || delta === 0) return;
  const fromOpt = optimisticCustomers.get(id);
  const fromCache = cachedSnapshot?.customers.find((row) => String(row.id) === id);
  const base = fromOpt ?? fromCache;
  if (!base) return;
  upsertWebCustomerInCache({
    ...base,
    balance: Number(base.balance ?? 0) + delta,
    updated_at: new Date().toISOString(),
  });
}

/**
 * Aplica snapshot remoto respetando generación e incomplete.
 * - complete: reemplaza lista (con overlay optimista)
 * - incomplete: merge en cache completa existente; nunca la reemplaza
 * - pullGeneration !== actual: descartado (hubo write local después de que empezó el pull)
 */
export function applyWebCustomerRemoteSnapshot(
  snapshot: CustomersSnapshotPayload,
  pullGeneration: number,
): boolean {
  if (pullGeneration !== snapshotGeneration) return false;

  const isComplete = snapshot.snapshot_complete === true;

  if (isComplete) {
    const customers = overlayOptimisticCustomers(
      Array.isArray(snapshot.customers) ? snapshot.customers : [],
    );
    cachedSnapshot = {
      ...snapshot,
      customers,
      customer_movements: Array.isArray(snapshot.customer_movements)
        ? snapshot.customer_movements
        : [],
      sale_payment_allocations: Array.isArray(snapshot.sale_payment_allocations)
        ? snapshot.sale_payment_allocations
        : [],
      snapshot_complete: true,
    };
    cacheUpdatedAt = Date.now();
    return true;
  }

  if (!cachedSnapshot || cachedSnapshot.snapshot_complete === false) {
    // Sin base completa: no cachear un delta como si fuera la lista entera.
    return false;
  }

  cachedSnapshot = {
    ...cachedSnapshot,
    customers: overlayOptimisticCustomers(
      mergeCustomerRows(cachedSnapshot.customers, snapshot.customers ?? []),
    ),
    customer_movements: [
      ...(cachedSnapshot.customer_movements ?? []),
      ...(snapshot.customer_movements ?? []),
    ],
    sale_payment_allocations: [
      ...(cachedSnapshot.sale_payment_allocations ?? []),
      ...(snapshot.sale_payment_allocations ?? []),
    ],
    snapshot_complete: true,
  };
  cacheUpdatedAt = Date.now();
  return true;
}

/** Obtiene snapshot remoto con cache sesión; evita triplet REST y RPC repetidos. */
export async function loadWebCustomerSnapshot(forceRefresh = false): Promise<CustomersSnapshotPayload> {
  const fresh =
    !forceRefresh &&
    cachedSnapshot != null &&
    cachedSnapshot.snapshot_complete !== false &&
    Date.now() - cacheUpdatedAt < CUSTOMER_WEB_REST_CACHE_MS;

  if (fresh && cachedSnapshot) {
    return {
      ...cachedSnapshot,
      customers: overlayOptimisticCustomers(cachedSnapshot.customers),
    };
  }

  // Lectores concurrentes sobre cache fría comparten el pull en vuelo —
  // sin esto cada consumidor dispara su propio get_mobile_customers_snapshot.
  if (snapshotPullInFlight) return snapshotPullInFlight;

  const pullGeneration = snapshotGeneration;
  snapshotPullInFlight = (async () => {
    const snapshot = await fetchRemoteCustomersSnapshot();
    const applied = applyWebCustomerRemoteSnapshot(
      { ...snapshot, snapshot_complete: true },
      pullGeneration,
    );
    if (applied && cachedSnapshot) return cachedSnapshot;
    // Write concurrente: devolver lo que haya (incluye optimistas).
    if (cachedSnapshot) {
      return {
        ...cachedSnapshot,
        customers: overlayOptimisticCustomers(cachedSnapshot.customers),
      };
    }
    return {
      ...snapshot,
      customers: overlayOptimisticCustomers(snapshot.customers ?? []),
      snapshot_complete: true,
    };
  })().finally(() => {
    snapshotPullInFlight = null;
  });
  return snapshotPullInFlight;
}

export function clearWebCustomerSnapshotCache(): void {
  snapshotGeneration += 1;
  cachedSnapshot = null;
  cacheUpdatedAt = 0;
  optimisticCustomers.clear();
}

/** Invalida pulls en vuelo sin borrar la lista (writes web). */
export function bumpWebCustomerSnapshotGeneration(): void {
  snapshotGeneration += 1;
}
